import Link from "next/link";
import useSWR from "swr";

import { useCallback, useMemo } from "react";

import type { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import { SKSpinner } from "components/customElements/skSpinner";
import { Title } from "components/customElements/title";
import { baseFetcher } from "utils/baseFetcher";

const RelatedInsightsList = ({
  insight,
  section,
}: {
  insight: Insight;
  section: { id: string; name: string; ordered?: boolean; columns?: number };
}) => {
  const { entity } = insight;

  const viewMoreLink = useMemo(() => {
    if (section.id === "related") {
      return `/entities/${entity?.slug}/research`;
    }
    if (section.id === "trending") {
      return `/dashboard?collection=trending`;
    }
    if (section.id === "unpaywalled") {
      return `/collections/unpaywalled/research`;
    }
    if (section.id === "top-collection") {
      return `/collections`;
    }
    return "";
  }, [entity?.slug, section.id]);

  const relatedUrl = useMemo(() => {
    if (section.id === "related") {
      return `/api/v2/entities/${entity?.slug}/primary-insights?include=account,company,locker-items&sort=-published-at,-updated-at&page[size]=5`;
    }
    if (section.id === "trending") {
      return `/api/v2/trending-insights?page[size]=5`;
    }
    if (section.id === "unpaywalled") {
      return `/api/v2/collections/unpaywalled/insights?sort=-published-at,-updated-at&page[size]=5&filter[is-primer]=false`;
    }
    if (section.id === "top-collection") {
      return `/api/v2/collections?filter[segment]=trending&sort=-updated-at&page[size]=10`;
    }
  }, [entity?.slug, section.id]);

  const { data: items, isValidating } = useSWR(relatedUrl, baseFetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });

  const isEmtpty =
    !isValidating && !items?.news?.length && !items?.data?.length;

  const renderItem = useCallback(
    (item: Insight | any) => {
      if (item.type === "collections") {
        return (
          <li
            key={`${section.id}-${item.id}`}
            className={twMerge(
              (section.columns || 1) > 1 && "basis-[50%] mb-[10px]"
            )}
          >
            <Link
              href={`/collections/${item.slug}`}
              className={twMerge(
                "text-subtle-black text-[13px] !font-pt-serif"
              )}
            >
              {item.name}
            </Link>
          </li>
        );
      }

      return (
        <li key={`${section.id}-${item.id}`}>
          <Link
            href={`/insights/${item.slug}`}
            className="text-subtle-black text-[13px] !font-pt-serif"
          >
            {item.tagline}
          </Link>
        </li>
      );
    },
    [section.columns, section.id]
  );

  const list = useMemo(
    () =>
      isEmtpty ? (
        <div className="italic text-shady-gray text-[13px]">No items found</div>
      ) : isValidating ? (
        <SKSpinner />
      ) : (
        items?.data?.map(renderItem)
      ),
    [isEmtpty, isValidating, items?.data, renderItem]
  );

  return (
    <section>
      <Title className="flex">
        <div className="flex-1">{section.name}</div>
        <a
          href={viewMoreLink}
          className="pull-right normal-case text-smart-teal"
        >
          More »
        </a>
      </Title>
      <div>
        {section.ordered ? (
          <ol
            className={twMerge(
              "flex flex-wrap",
              (section.columns || 1) === 1 && "flex-col space-y-[10px]",
              "overflow-x-auto",
              "list-none pl-0"
            )}
          >
            {list}
          </ol>
        ) : (
          <ul
            className={twMerge(
              "flex flex-col flex-wrap space-y-[10px]",
              "overflow-x-auto",
              "list-none pl-0"
            )}
          >
            {list}
          </ul>
        )}
      </div>
    </section>
  );
};

export default RelatedInsightsList;
